/**
 * AI Text Generation API Service
 */

import { privateApi } from '@/config/axios.config';
import { sessionService } from '@/shared/services/session.service';
import { CONSENT_REQUIRED, esConsentimientoRequerido } from '@/features/ai-consent/consent-required';
import { TextGenerationRequest, TextGenerationResponse, TextGenerationStyle, PropertyDescriptionResponse } from '../types/ai-text-generation.types';
import { esProveedorReal } from '../utils/proveedor-real';

const API_BASE_URL = '/ai/text-generation';

/** El stream no puede cambiar el HTTP 200 ya enviado: el error del proxy llega como evento SSE con `status` y `code`. */
const parsearEvento = (data: string): any | null => {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

/**
 * PMO #23388: lo que ve la persona cuando el stream corta con un error. Antes de esto, cualquier evento de
 * error que no fuera CONSENT_REQUIRED se ignoraba y la generación terminaba con texto vacío, sin aviso:
 * quien agotó su cupo no se enteraba de que tenía que comprar un paquete o cambiar de plan.
 */
const MENSAJES_ERROR_IA: Record<string, string> = {
  QUOTA_EXCEEDED_BUY_PACKAGE:
    'Llegaste al límite de generaciones con IA de tu plan. Compra un paquete adicional en Facturación para seguir usándola.',
  QUOTA_EXCEEDED_UPGRADE_PLAN:
    'Llegaste al límite de generaciones con IA de tu plan. Cambia a un plan superior en Facturación para seguir usándola.',
  QUOTA_NOT_ENABLED: 'Tu plan no incluye la generación con IA. Puedes activarla cambiando de plan en Facturación.',
  PURPOSE_NOT_IN_PLAN: 'Tu plan no incluye este uso de la IA. Puedes activarlo cambiando de plan en Facturación.',
  // #23425 (códigos de #2967): reintentar no arregla un proveedor sin configurar, así que ese aviso no dice
  // «intenta de nuevo». Textos en borrador: los afina MUSE.
  AI_PROVIDER_NOT_CONFIGURED: 'La generación con IA todavía no está disponible en tu cuenta. Escríbenos a soporte si la necesitas.',
  AI_PROVIDER_FAILED: 'El servicio de IA no respondió. Intenta de nuevo en unos minutos.',
};

export const MENSAJE_ERROR_IA_GENERICO = 'No pudimos generar el texto con IA. Intenta de nuevo en unos minutos.';

export const mensajeErrorIa = (code: unknown): string =>
  (typeof code === 'string' && MENSAJES_ERROR_IA[code]) || MENSAJE_ERROR_IA_GENERICO;

/**
 * Cuando el proveedor de IA falla, el API responde 200 con un texto de RELLENO (`metadata.provider === 'mock'`,
 * ai.service.ts buildMockText): cuatro párrafos que inventan amenidades de la propiedad. Ese texto nunca se
 * inserta como si fuera generado: se trata como un error y la persona ve el aviso genérico.
 */
const esRelleno = (metadata: any): boolean => metadata?.provider === 'mock';

const errorDeRelleno = (): Error => {
  const err: any = new Error(MENSAJE_ERROR_IA_GENERICO);
  err.esRelleno = true;
  err.desdeStream = true;
  return err;
};

/**
 * PMO #23381 + #23388: un evento del stream con `error` (o con `code`) corta la generación. Se lanza con la forma
 * de un error de axios (`response.status/data.code`), así `esConsentimientoRequerido` reconoce CONSENT_REQUIRED
 * igual que un 403 normal. Cualquier otro código rechaza con su mensaje en español. `desdeStream` evita el
 * respaldo por POST: el servidor ya respondió y repetir la llamada daría el mismo error o gastaría otra generación.
 */
const lanzarSiEsError = (evento: any): void => {
  if (!evento || (evento.error === undefined && evento.code === undefined)) return;
  const err: any =
    evento.code === CONSENT_REQUIRED
      ? new Error(evento.error || 'Se requiere consentimiento para usar la IA')
      : new Error(mensajeErrorIa(evento.code));
  err.code = evento.code;
  err.status = evento.status ?? (evento.code === CONSENT_REQUIRED ? 403 : undefined);
  err.response = { status: err.status, data: { message: err.message, code: evento.code } };
  err.desdeStream = true;
  throw err;
};

/**
 * Generate text using AI with streaming support
 * Returns a promise that resolves when streaming is complete
 */
export const generateText = async (
  request: TextGenerationRequest,
  onChunk?: (chunk: string) => void
): Promise<TextGenerationResponse> => {
  try {
    // La MISMA credencial que manda privateApi (axios.config.ts, interceptor de request). Antes se buscaba una cookie
    // `authToken`/`token` o `localStorage.authToken`, donde la sesión de prod no vive: el /stream daba 401 siempre
    // (medido por IRIS en prod, 16-oct 14:52Z) y toda generación caía al respaldo por POST.
    const token = sessionService.getToken();
    // Obtener la URL base de la configuración de axios (ya incluye /api/v1)
    const apiBaseUrl = privateApi.defaults.baseURL;
    if (!apiBaseUrl) {
      throw new Error('API base URL not configured. Check VITE_API_URL environment variable.');
    }

    // Usar fetch para streaming (apiBaseUrl ya incluye /api/v1)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout

    const response = await fetch(
      `${apiBaseUrl}${API_BASE_URL}/stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(request),
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      // Preserve status for extractAiError in the caller
      const errBody = await response.json().catch(() => ({}));
      const err: any = new Error(errBody?.message || `HTTP error! status: ${response.status}`);
      err.response = { status: response.status, data: errBody, headers: Object.fromEntries(response.headers.entries()) };
      err.status = response.status;
      throw err;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';
    // El evento final `{ done, metadata }` dice si el texto es relleno. Hasta leerlo, los chunks se RETIENEN: el
    // tooltip inserta en el editor con el primer chunk, así que entregarlos antes haría llegar el relleno igual.
    // El servidor genera el texto entero antes de partirlo (ai.controller.ts), retenerlo no agrega espera.
    let metadataFinal: any = null;

    if (!reader) {
      throw new Error('No reader available');
    }

    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;

      if (done) break;

      buffer += decoder.decode(result.value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.trim() === '') continue;
        
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            break;
          }
          
          const parsed = parsearEvento(data);
          if (parsed) {
            lanzarSiEsError(parsed);
            if (parsed.metadata) metadataFinal = parsed.metadata;
            if (parsed.chunk) fullText += parsed.chunk;
          }
        } else if (line.trim().startsWith('{')) {
          // JSON directo sin prefijo "data: "
          try {
            const parsed = JSON.parse(line.trim());
            if (parsed.metadata) metadataFinal = parsed.metadata;
            if (parsed.chunk) fullText += parsed.chunk;
          } catch (e) {
            // Ignorar
          }
        }
      }
    }

    // Procesar último buffer
    if (buffer.trim()) {
      const lines = buffer.split('\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          const parsed = parsearEvento(data);
          if (parsed) {
            lanzarSiEsError(parsed);
            if (parsed.metadata) metadataFinal = parsed.metadata;
            if (parsed.chunk) fullText += parsed.chunk;
          }
        }
      }
    }

    clearTimeout(timeoutId);

    // Sin el evento final no se sabe si el texto es relleno (el servidor siempre lo manda: ai.controller.ts). Un stream
    // cortado antes de él falla hacia el error, no hacia entregar un texto que pudo ser relleno o quedar truncado.
    if (!metadataFinal || esRelleno(metadataFinal)) throw errorDeRelleno();
    if (fullText) onChunk?.(fullText);

    return {
      text: fullText,
      style: request.style,
      metadata: metadataFinal ?? {
        model: 'gpt-4',
        provider: 'openai',
      },
    };
  } catch (error: any) {
    // PMO #23381: sin consentimiento NO se cae al POST normal (daría el mismo 403 y gastaría otra llamada):
    // el error sube con su código para que quien llamó abra el modal de consentimiento y reintente.
    if (esConsentimientoRequerido(error) || error?.desdeStream) throw error;
    // Fallback a API normal si streaming falla
    try {
      const response = await privateApi.post<{ success: boolean; data: TextGenerationResponse }>(
        `${API_BASE_URL}`,
        request
      );
      if (response.data?.success === false || esRelleno(response.data?.data?.metadata)) throw errorDeRelleno();
      return response.data.data;
    } catch (fallbackError: any) {
      if (esConsentimientoRequerido(fallbackError) || fallbackError?.esRelleno) throw fallbackError;
      // PMO #23388 también por el respaldo: el error de cuota sale con el mismo mensaje en español que en el stream,
      // no con el texto crudo del servidor.
      const data = fallbackError?.response?.data;
      const code = data?.code ?? data?.details?.code;
      const err: any = new Error(mensajeErrorIa(code));
      err.code = code;
      err.status = fallbackError?.response?.status;
      err.response = { status: err.status, data: { message: err.message, code } };
      throw err;
    }
  }
};

/**
 * Generate text without streaming (simple POST)
 */
export const generateTextSimple = async (
  request: TextGenerationRequest
): Promise<TextGenerationResponse> => {
  const response = await privateApi.post<{ success: boolean; data: TextGenerationResponse }>(
    `${API_BASE_URL}`,
    request
  );
  // PMO #23418 — FAIL-CLOSED: sin `metadata.provider` de un proveedor real el texto no se entrega, no solo con `mock`.
  if (response.data?.success === false || esRelleno(response.data?.data?.metadata) || !esProveedorReal(response.data?.data?.metadata)) {
    throw errorDeRelleno();
  }
  return response.data.data;
};

/**
 * Generate property description from property data using AI
 */
export const generatePropertyDescription = async (
  propertyId: string,
  options?: { style?: TextGenerationStyle; save?: boolean },
): Promise<PropertyDescriptionResponse> => {
  const response = await privateApi.post<{
    success: boolean;
    data: PropertyDescriptionResponse;
  }>(`/ai/property-description/${propertyId}`, {
    style: options?.style || 'professional',
    save: options?.save || false,
  });
  return response.data.data;
};

/**
 * Generate property description using Builder AI Engine (Gemini).
 * Uses property data already saved in the DB — requires propertyId.
 */
export const generateBuilderDescription = async (
  propertyId: string,
): Promise<{ description: string; tokensUsed: number }> => {
  const response = await privateApi.post<{
    success: boolean;
    data: { description: string; tokensUsed: number };
  }>('/builder/admin/ai/generate-description', { propertyId });
  return response.data.data;
};

/**
 * Get AI usage stats for the current tenant.
 */
export const getBuilderAiUsage = async (): Promise<{
  today: number;
  limit: number;
  remaining: number;
  totalTokens: number;
}> => {
  const response = await privateApi.get<{
    success: boolean;
    data: { today: number; limit: number; remaining: number; totalTokens: number };
  }>('/builder/admin/ai/usage');
  return response.data.data;
};

/**
 * Enhance a property photo using AI (Builder AI Engine).
 */
export const enhancePropertyPhoto = async (
  propertyId: string,
  imageUrl: string,
): Promise<{ originalUrl: string; enhancedUrl: string; transformations: string[] }> => {
  const response = await privateApi.post<{
    success: boolean;
    data: { originalUrl: string; enhancedUrl: string; transformations: string[] };
  }>('/builder/admin/ai/enhance-photo', { propertyId, imageUrl });
  return response.data.data;
};

/**
 * Generate an AI image using Gemini Imagen 3 → Cloudinary.
 * POST /builder/admin/ai/generate-image
 */
export const generateAiImage = async (params: {
  prompt: string;
  aspectRatio: string;
  category: string;
}): Promise<{ imageUrl: string; tokensUsed: number }> => {
  const response = await privateApi.post<{
    success: boolean;
    data: { imageUrl: string; tokensUsed: number };
  }>('/builder/admin/ai/generate-image', params, { timeout: 90000 });
  return response.data.data;
};

/**
 * AI Assistant — contextual suggestions per block.
 * POST /builder/admin/editor/ai-assist
 */
export const aiAssistBlock = async (params: {
  blockType: string;
  currentContent: Record<string, string>;
  userPrompt: string;
  language?: string;
}): Promise<{ suggestions: Array<{ field: string; value: string }>; tokensUsed: number }> => {
  const response = await privateApi.post<{
    success: boolean;
    data: { suggestions: Array<{ field: string; value: string }>; tokensUsed: number };
  }>('/builder/admin/editor/ai-assist', {
    ...params,
    language: params.language || 'es',
  });
  return response.data.data;
};

export const aiTextGenerationApi = {
  generateText,
  generateTextSimple,
  generatePropertyDescription,
  generateBuilderDescription,
  getBuilderAiUsage,
  enhancePropertyPhoto,
  generateAiImage,
  aiAssistBlock,
};

